/**
 * Accept-time apply for J2 destinations that are not already handled as
 * Delivery program writes (project files, award intake, pricing, capture,
 * RFI). Program Docs / SOW stay on create_program_document in DeliveryActionCard.
 */ import { Timestamp } from '@bufbuild/protobuf';
import { artifactClient, govconClient, projectDataClient } from '../../../lib/connect/clients';
import { uploadAttachmentViaTesoro } from '../../../lib/uploadAttachment';
import { attachResolvedChatFiles, resolveChatFiles } from './assistChatFiles';
import { deliveryActionNeedsProgram, receiptHrefForAction } from './deliveryActionDestinations';
function str(params, key) {
    const v = params?.[key];
    return typeof v === 'string' ? v : '';
}
function strAny(params, ...keys) {
    for (const key of keys){
        const v = str(params, key);
        if (v) return v;
    }
    return '';
}
/** An RFC3339 param as a proto Timestamp; undefined when absent or unparseable. */ function timestampAny(params, ...keys) {
    const raw = strAny(params, ...keys);
    if (!raw) return undefined;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? undefined : Timestamp.fromDate(d);
}
/** Copy a chat artifact into a tesoro object the destination RPCs accept (UI upload path). */ export async function rehostChatFile(file, opts) {
    const dl = await artifactClient.generateDownloadUrl({
        artifactId: file.chatArtifactId
    });
    if (!dl.url) {
        throw new Error(`Could not read ${file.filename}`);
    }
    const resp = await fetch(dl.url);
    if (!resp.ok) {
        throw new Error(`Could not read ${file.filename}`);
    }
    const blob = await resp.blob();
    const uploaded = await uploadAttachmentViaTesoro(new File([
        blob
    ], file.filename, {
        type: file.mimeType || blob.type
    }), {
        tenantId: opts.tenantId,
        ownerUserId: opts.ownerUserId,
        projectId: opts.projectId,
        autoProcess: false
    });
    if (!uploaded.artifactId) {
        throw new Error(`Could not place ${file.filename} on the destination`);
    }
    return uploaded.artifactId;
}
async function placeFiles(files, ctx) {
    if (ctx.programId) {
        return attachResolvedChatFiles(files, {
            programId: ctx.programId,
            projectId: ctx.projectId
        });
    }
    if (!ctx.tenantId || !ctx.ownerUserId) {
        throw new Error('Sign in is required to file this upload.');
    }
    const out = [];
    for (const f of files){
        const artifactId = await rehostChatFile(f, {
            tenantId: ctx.tenantId,
            ownerUserId: ctx.ownerUserId,
            projectId: ctx.projectId
        });
        out.push({
            ...f,
            artifactId
        });
    }
    return out;
}
function hrefResult(action, extra, ctx) {
    const href = receiptHrefForAction(action, ctx);
    return href ? {
        ...extra,
        href
    } : extra;
}
export async function applyUploadParityAction(programId, action, params, options) {
    const files = await resolveChatFiles(params);
    if (files.length === 0) {
        throw new Error('provide files (chat attachment ids from list-files)');
    }
    const projectId = strAny(params, 'project_id', 'projectId');
    const place = {
        // Project / capture / RFI / pricing rows rehost. A mixed card still
        // carries programId for Docs rows; do not promote those files onto the program.
        programId: deliveryActionNeedsProgram(action) ? programId || undefined : undefined,
        projectId: projectId || undefined,
        tenantId: options?.tenantId,
        ownerUserId: options?.ownerUserId
    };
    switch(action){
        case 'upload_project_files':
            {
                if (!projectId) throw new Error('project_id is required');
                const placed = await placeFiles(files, place);
                const { projectApiConnect } = await import('../../../lib/api/projectApi.connect');
                const { proposalProjectApiConnect } = await import('../../../lib/api/proposalProjectApi.connect');
                const ids = placed.map((f)=>f.artifactId);
                await projectApiConnect.attachArtifactsToProject(projectId, ids);
                const batch = await proposalProjectApiConnect.processBatch(projectId, ids);
                return hrefResult(action, {
                    batchId: batch.batchId
                }, {
                    projectId
                });
            }
        case 'upload_award_intake':
            {
                if (!projectId) throw new Error('project_id is required');
                const preAwardProjectId = strAny(params, 'pre_award_project_id', 'preAwardProjectId');
                if (!preAwardProjectId) throw new Error('pre_award_project_id is required');
                const placed = await placeFiles(files, place);
                const { projectApiConnect } = await import('../../../lib/api/projectApi.connect');
                const { proposalProjectApiConnect } = await import('../../../lib/api/proposalProjectApi.connect');
                const ids = placed.map((f)=>f.artifactId);
                await projectApiConnect.attachArtifactsToProject(projectId, ids);
                const batch = await proposalProjectApiConnect.processBatch(projectId, ids, {
                    awardIntakeOnly: true
                });
                // Same as AwardIntakePanel: register the tray job and leave comparison
                // as the Launch-screen second click once the batch is ready.
                const { useProcessingJobsStore } = await import('../../../lib/processing/jobsStore');
                useProcessingJobsStore.getState().registerJob({
                    batchId: batch.batchId,
                    zouProjectId: projectId,
                    kind: 'award-intake',
                    label: 'Processing award document',
                    link: `/delivery/pre-award/${preAwardProjectId}/v2?screen=launch`,
                    meta: {
                        preAwardProjectId,
                        awardDocId: ids[0],
                        artifactIds: ids
                    }
                });
                return hrefResult(action, {
                    batchId: batch.batchId,
                    id: ids[0]
                }, {
                    preAwardProjectId
                });
            }
        case 'extract_pricing_sheet':
            {
                if (files.length > 1) {
                    throw new Error('extract_pricing_sheet takes one file per row');
                }
                const placed = await placeFiles(files, {
                    tenantId: options?.tenantId,
                    ownerUserId: options?.ownerUserId
                });
                const file = placed[0];
                const dl = await artifactClient.generateDownloadUrl({
                    artifactId: file.artifactId
                });
                if (!dl.url) throw new Error(`Could not read ${file.filename}`);
                const resp = await fetch(dl.url);
                if (!resp.ok) throw new Error(`Could not read ${file.filename}`);
                const bytes = new Uint8Array(await resp.arrayBuffer());
                const extracted = await govconClient.extractRateObservations({
                    fileData: bytes,
                    filename: file.filename,
                    mimeType: file.mimeType,
                    tenantId: options?.tenantId ?? '',
                    contractVehicleId: strAny(params, 'contract_vehicle_id', 'contractVehicleId'),
                    companyProfileId: strAny(params, 'company_profile_id', 'companyProfileId'),
                    sourceDocumentId: file.artifactId,
                    observationType: strAny(params, 'observation_type', 'observationType'),
                    seedMissingCategories: true,
                    background: true,
                    // Provenance of a sheet pulled from the web: recorded on the sheet's
                    // document row so chat can later say where and when it came from.
                    sourceUrl: strAny(params, 'source_url', 'sourceUrl'),
                    sourcePageUrl: strAny(params, 'source_page_url', 'sourcePageUrl'),
                    retrievedAt: timestampAny(params, 'retrieved_at', 'retrievedAt'),
                    sourceSha256: strAny(params, 'source_sha256', 'sourceSha256')
                });
                return hrefResult(action, {
                    runId: extracted.runId,
                    id: file.artifactId
                }, {});
            }
        case 'file_company_document':
            {
                const companyProfileId = strAny(params, 'company_profile_id', 'companyProfileId');
                if (!companyProfileId) throw new Error('company_profile_id is required');
                const placed = await placeFiles(files, place);
                for (const f of placed){
                    await artifactClient.linkArtifactToEntity({
                        artifactId: f.artifactId,
                        entityType: 'company_profile',
                        entityId: companyProfileId
                    });
                }
                return hrefResult(action, {
                    id: placed[0].artifactId
                }, {
                    companyProfileId
                });
            }
        case 'link_capture_document':
            {
                const entityType = strAny(params, 'entity_type', 'entityType');
                const entityId = strAny(params, 'entity_id', 'entityId');
                if (!entityType || !entityId) {
                    throw new Error('entity_type and entity_id are required');
                }
                const placed = await placeFiles(files, place);
                for (const f of placed){
                    await artifactClient.linkArtifactToEntity({
                        artifactId: f.artifactId,
                        entityType,
                        entityId
                    });
                }
                return hrefResult(action, {
                    id: placed[0].artifactId
                }, {
                    captureProjectId: entityType === 'capture_project' ? entityId : undefined
                });
            }
        case 'attach_rfi_package':
            {
                if (files.length > 1) throw new Error('attach_rfi_package takes one file per row');
                const rfiResponseProjectId = strAny(params, 'rfi_response_project_id', 'rfiResponseProjectId');
                const packageId = strAny(params, 'package_id', 'packageId');
                if (!rfiResponseProjectId || !packageId || !projectId) {
                    throw new Error('rfi_response_project_id, project_id, and package_id are required');
                }
                const placed = await placeFiles(files, place);
                const { rfiSectionClient } = await import('../../../pages/RfiResponse/rfiSectionHooks');
                await rfiSectionClient.attachRfiPackageArtifact({
                    rfiResponseProjectId,
                    packageId,
                    artifactId: placed[0].artifactId,
                    requestId: `assist:${packageId}:${placed[0].artifactId}`
                });
                return hrefResult(action, {
                    id: placed[0].artifactId
                }, {
                    rfiResponseProjectId,
                    projectId
                });
            }
        case 'save_external_capture_output':
            {
                if (files.length > 1) {
                    throw new Error('save_external_capture_output takes one file per row');
                }
                const captureProjectId = strAny(params, 'capture_project_id', 'captureProjectId');
                const templateId = strAny(params, 'template_id', 'templateId');
                if (!captureProjectId || !templateId || !projectId) {
                    throw new Error('capture_project_id, project_id, and template_id are required');
                }
                const placed = await placeFiles(files, place);
                let expectedHeadId = strAny(params, 'expected_head_id', 'expectedHeadId');
                if (!expectedHeadId) {
                    const review = await projectDataClient.reviewCaptureOutput({
                        captureProjectId,
                        templateId
                    });
                    expectedHeadId = review.headId ?? '';
                }
                await projectDataClient.saveCaptureOutputVersion({
                    captureProjectId,
                    templateId,
                    operation: 'external',
                    artifactId: placed[0].artifactId,
                    expectedHeadId
                });
                return hrefResult(action, {
                    id: placed[0].artifactId
                }, {
                    captureProjectId,
                    projectId
                });
            }
        default:
            throw new Error(`Unknown upload destination ${action}`);
    }
}
